import {
  canonicalizeCampaignDate,
  type Campaign,
  type CampaignDraftCreatorItem,
  type CampaignDraftPostItem,
  type CampaignTrackHandle,
  type CampaignTrackPost,
} from '@/entities/campaign';

import type { CampaignEditorSubmitPayload } from '../../../campaign-editor/campaign-editor-types';
import { emptyCreatorItem, emptyPostItem } from '../../../hooks';

type BuildCampaignEditInitialValuesArgs = {
  campaign: Campaign;
  creators: CampaignTrackHandle[];
  posts: CampaignTrackPost[];
  workspaceTimezone?: string;
};

function buildInitialCreatorItems(creators: CampaignTrackHandle[]): CampaignDraftCreatorItem[] {
  if (creators.length === 0) {
    return [emptyCreatorItem()];
  }

  return creators.map((creator) => ({
    id: crypto.randomUUID(),
    label: creator.label,
    platform: creator.platform,
    sourceValue: creator.handle.replace(/^@+/, ''),
  }));
}

function buildInitialPostItems(posts: CampaignTrackPost[]): CampaignDraftPostItem[] {
  if (posts.length === 0) {
    return [emptyPostItem()];
  }

  return posts.map((post) => ({
    id: crypto.randomUUID(),
    postUrl: post.post_url,
  }));
}

export function buildCampaignEditInitialValues({
  campaign,
  creators,
  posts,
  workspaceTimezone,
}: BuildCampaignEditInitialValuesArgs): CampaignEditorSubmitPayload {
  const campaignEndDate = campaign.end_date
    ? canonicalizeCampaignDate(campaign.end_date, workspaceTimezone)
    : undefined;

  return {
    campaignEndDate,
    campaignName: campaign.name,
    campaignStartDate: canonicalizeCampaignDate(campaign.start_date, workspaceTimezone),
    creatorItems: buildInitialCreatorItems(creators),
    creatorKeywords: campaign.creator_tracking_keywords ?? [],
    hasEndDate: Boolean(campaign.end_date),
    postItems: buildInitialPostItems(posts),
  };
}
